import React, { useEffect, useState } from 'react';
import { Modal, Box, Typography } from '@availity/element';
import Button from '../../Utils/components/Button';
import demoKeyApi from '../../../api/developerHub/demoKeyApi';
import { iconFilters } from '../../../static/constants/styles';
import { colors } from '../../../static/constants/theme';
import { typography } from '../../../static/constants/typography';
import alertCircleIcon from '../../../static/assets/icons/alert-circle.svg';
import closeIcon from '../../../static/assets/icons/close.svg';
import copyIcon from '../../../static/assets/icons/copy.svg';
import checkCopyIcon from '../../../static/assets/icons/check-copy.svg';

interface Props {
  open: boolean;
  onClose: () => void;
  theme: 'light' | 'dark';
}

const GenerateDemoKeyModal = ({ open, onClose, theme }: Props): JSX.Element => {
  const [key, setKey] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const generate = async () => {
    setLoading(true);
    setError(null);
    setKey(null);
    setCopied(false);
    try {
      const data = await demoKeyApi.generateDemoKey();
      setKey(data.key);
    } catch (error_: unknown) {
      setError(error_ instanceof Error ? error_.message : 'Failed to generate key');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) generate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const copy = () => {
    if (key) {
      navigator.clipboard.writeText(key);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleClose = () => {
    setKey(null);
    setError(null);
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      data-testid="generate-demo-key-modal"
      sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', '& .MuiBackdrop-root': { backgroundColor: colors.background.backdrop } }}
    >
      <Box
        className="generate-demo-key-modal__container"
        data-theme={theme}
      >
        {/* Close button */}
        <Box
          className="generate-demo-key-modal__close-btn"
          onClick={handleClose}
          role="button"
          aria-label="Close modal"
        >
          <img src={closeIcon} alt="close" width={16} height={16} style={{ filter: theme === 'light' ? iconFilters.dark : iconFilters.white }} />
        </Box>

        {/* Content */}
        <Box className="generate-demo-key-modal__content">
          {/* Heading */}
          <Box className="generate-demo-key-modal__heading">
            <Typography className="generate-demo-key-modal__heading-title" sx={{ ...typography.latoSemibold28 }}>
              {error ? 'Generate Demo API Key' : key ? 'Generate Demo API Key' : 'Generate Demo API Key'}
            </Typography>
            <Typography className="generate-demo-key-modal__heading-subtitle" sx={{ ...typography.latoRegular16Alt }}>
              {loading
                ? 'Creates a short-lived demo key for the Unified Gateway. Valid for 40 minutes.'
                : error
                  ? 'Creates a short-lived demo key for the Unified Gateway. '
                  : 'Creates a short-lived demo key for the Unified Gateway. '}
            </Typography>
            {loading && (
              <Typography className="generate-demo-key-modal__heading-subtitle" sx={{ ...typography.latoRegular16Alt }}>
                Generating...
              </Typography>
            )}
            {key && (
              <Box component="ul" className="generate-demo-key-modal__info-list">
                <Typography component="li" className="generate-demo-key-modal__info-item" sx={{ ...typography.latoRegular16Alt }}>Your key is only valid for 40 minutes.</Typography>
                <Typography component="li" className="generate-demo-key-modal__info-item" sx={{ ...typography.latoRegular16Alt }}>You must store the key securely &mdash; it won&apos;t be shown again.</Typography>
                <Typography component="li" className="generate-demo-key-modal__info-item" sx={{ ...typography.latoRegular16Alt }}>Please allow 2-3 minutes for the key to sync before making your first API call.</Typography>
              </Box>
            )}
          </Box>

          {/* Error alert */}
          {error && (
            <Box className="generate-demo-key-modal__alert generate-demo-key-modal__alert--error">
              <Box className="generate-demo-key-modal__alert-row">
                <img className="generate-demo-key-modal__alert-icon" src={alertCircleIcon} alt="error" />
                <Box className="generate-demo-key-modal__alert-text">
                  <Typography className="generate-demo-key-modal__alert-title">There has been a network error.</Typography>
                  <Typography className="generate-demo-key-modal__alert-message">{error}</Typography>
                </Box>
              </Box>
            </Box>
          )}

          {/* Success key display */}
          {key && (
            <Box className="generate-demo-key-modal__key-section">
              {/* Key label + input */}
              <Box className="generate-demo-key-modal__key-label-block">
                <Typography className="generate-demo-key-modal__key-label" sx={{ ...typography.latoBold16 }}>Your demo key (shown once):</Typography>
                <Box className="generate-demo-key-modal__key-input-row">
                  <Box className="generate-demo-key-modal__key-input-wrapper">
                    <Box
                      component="input"
                      readOnly
                      value={key ?? ''}
                      className="generate-demo-key-modal__key-input"
                    />
                    <Box className="generate-demo-key-modal__status-chip">Success</Box>
                  </Box>
                  <Box
                    className="generate-demo-key-modal__copy-btn"
                    onClick={copy}
                    role="button"
                    aria-label="Copy key"
                  >
                    <img src={copied ? checkCopyIcon : copyIcon} alt={copied ? 'copied' : 'copy'} width={16} height={16} style={{ filter: theme === 'light' ? iconFilters.dark : iconFilters.white }} />
                  </Box>
                </Box>
              </Box>

              {/* Success alert - hidden per design */}
            </Box>
          )}
        </Box>

        {/* Button group */}
        <Box className="generate-demo-key-modal__button-group">
          {!loading && <Button variant="outlined" onClick={handleClose}>Close</Button>}
          {error && (
            <Button variant="navbar" onClick={generate}>
              Retry
            </Button>
          )}
        </Box>
      </Box>
    </Modal>
  );
};

export default GenerateDemoKeyModal;
